import { BusinessObjectBase } from './base';
import { testArticleSchema } from '@schemas/business-objects/test-article';
import type { PdfDocument } from './pdf-document';
import type { TestResult } from './test-result';
import type { Id, OralReadingFluency, TestType } from '../base-types';

export interface TestArticleData {
  readonly id: Id;
  readonly name?: string;
  readonly type?: TestType;
  readonly oralReadingFluency?: OralReadingFluency;
  readonly description?: string;
  readonly pdfDocument?: PdfDocument;
  readonly workingTestResults?: TestResult[];
  readonly achievedTestResults?: TestResult[];
}

export interface TestArticle extends Readonly<TestArticleData> {}
export class TestArticle extends BusinessObjectBase {
  static readonly entityName = 'TestArticle' as const;
  static readonly plural = 'test-articles';
  static schema = testArticleSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<TestArticle[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new TestArticle(data));
  }

  static async findOne(filter?: any): Promise<TestArticle | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new TestArticle(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<TestArticle> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new TestArticle(data);
  }

  static async create(data: Omit<TestArticleData, 'id'>): Promise<TestArticle> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new TestArticle(result);
  }

  static async createMany(
    data: Omit<TestArticleData, 'id'>[],
  ): Promise<TestArticle[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new TestArticle(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<TestArticleData>,
  ): Promise<TestArticle> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new TestArticle(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
